/**
 * Enabled-modules provider for per-agency package gating (D4-PKG-003).
 *
 * Fetches the deployment's enabled feature modules from
 * /api/v1/config/modules/ on mount and exposes them to the React tree. The
 * context object, types, constants and hooks (useModules / useModuleEnabled)
 * live in ./modules so this module exports ONLY the provider component.
 *
 * The frontend uses this to conditionally render sidebar nav entries and
 * dashboard widgets (e.g. hide commissions/leaderboard when the `financial`
 * module is disabled). The backend independently enforces gating at the API
 * boundary (403), so this is purely a UX layer — not a security boundary.
 *
 * Default (and fetch-failure fallback) = ALL modules enabled so existing
 * deployments and offline/error states keep the full UI (D4-PKG-006).
 */
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { apiRequest } from "../api/client";
import {
  DEFAULT_MODULES_CONFIG,
  ModulesContext,
} from "./modules";
import type { ModuleKey, ModulesConfig, ModulesContextType } from "./modules";

export function ModulesProvider({ children }: { children: ReactNode }) {
  const [enabledModules, setEnabledModules] = useState<ModuleKey[]>(
    DEFAULT_MODULES_CONFIG.enabled_modules,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    apiRequest<ModulesConfig>("/config/modules/", { method: "GET" }, controller.signal)
      .then((data) => {
        if (Array.isArray(data.enabled_modules)) {
          setEnabledModules(data.enabled_modules);
        }
      })
      .catch(() => {
        // Endpoint unreachable or errored (or request aborted) — keep the
        // all-on default so the UI never silently loses modules due to a
        // transient network failure.
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, []);

  const value = useMemo<ModulesContextType>(
    () => ({
      enabledModules,
      isEnabled: (module: ModuleKey) => enabledModules.includes(module),
      loading,
    }),
    [enabledModules, loading],
  );

  return (
    <ModulesContext.Provider value={value}>
      {children}
    </ModulesContext.Provider>
  );
}
